import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";

function KpiCard({ title, value, progress, color, suffix }) {
    return (
        <Card
            sx={{
                borderRadius: "18px",
                boxShadow:
                    "0 8px 32px rgba(255, 144, 21, 0.06), 0 2px 12px rgba(20, 24, 44, 0.06)",
                border: "1px solid rgba(255, 255, 255, 0.55)",
                background:
                    "linear-gradient(160deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.60) 100%)",
                backdropFilter: "blur(16px) saturate(1.4)",
                WebkitBackdropFilter: "blur(16px) saturate(1.4)",
                aspectRatio: "1 / 1",
                minHeight: 140,
                width: "100%",
                position: "relative",
                overflow: "hidden",
                transition:
                    "transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1), border-color 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                    transform: "translateY(-4px) scale(1.015)",
                    boxShadow:
                        "0 16px 48px rgba(255, 144, 21, 0.12), 0 4px 16px rgba(20, 24, 44, 0.08)",
                    borderColor: "rgba(255, 144, 21, 0.25)",
                },
                "&:active": {
                    transform: "translateY(-1px) scale(0.995)",
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background:
                        "linear-gradient(90deg, rgba(255, 144, 21, 0.0), rgba(255, 144, 21, 0.35), rgba(255, 193, 0, 0.25), rgba(255, 144, 21, 0.0))",
                    opacity: 0,
                    transition: "opacity 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                },
                "&:hover::before": {
                    opacity: 1,
                },
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative", zIndex: 1 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: "rgba(107, 114, 128, 0.85)",
                        fontWeight: 500,
                        fontSize: "0.78rem",
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        mb: 0.5,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        color: "rgba(20, 24, 44, 0.88)",
                        lineHeight: 1.1,
                    }}
                >
                    {value}
                    {suffix ? (
                        <Typography
                            component="span"
                            variant="subtitle2"
                            sx={{ color: "rgba(107, 114, 128, 0.85)", ml: 0.5, fontWeight: 600 }}
                        >
                            {suffix}
                        </Typography>
                    ) : null}
                </Typography>
                <LinearProgress
                    value={progress}
                    color={color}
                    variant="determinate"
                    sx={{
                        mt: 2.5,
                        height: 7,
                        borderRadius: 999,
                        backgroundColor: "rgba(148, 163, 184, 0.12)",
                        "& .MuiLinearProgress-bar": {
                            borderRadius: 999,
                            backgroundImage:
                                color === "success"
                                    ? "linear-gradient(90deg, rgba(58, 156, 96, 0.85), rgba(58, 156, 96, 1))"
                                    : color === "error"
                                      ? "linear-gradient(90deg, rgba(251, 105, 98, 0.85), rgba(251, 105, 98, 1))"
                                      : color === "info"
                                        ? "linear-gradient(90deg, rgba(0, 103, 184, 0.85), rgba(46, 170, 250, 1))"
                                        : "linear-gradient(90deg, rgba(255, 144, 21, 0.85), rgba(255, 193, 0, 1))",
                            boxShadow:
                                color === "success"
                                    ? "0 2px 8px rgba(58, 156, 96, 0.30)"
                                    : color === "error"
                                      ? "0 2px 8px rgba(251, 105, 98, 0.30)"
                                      : color === "info"
                                        ? "0 2px 8px rgba(0, 103, 184, 0.30)"
                                        : "0 2px 8px rgba(255, 144, 21, 0.30)",
                        },
                    }}
                />
            </CardContent>
        </Card>
    );
}

export default function DashboardKpis2({
    monthlyRequests,
    avgValidationDays,
    refusalRate,
    ongoingAbsences,
}) {
    const safeMonthly = monthlyRequests || 0;
    const safeAvg = Number.isFinite(avgValidationDays) ? avgValidationDays : 0;
    const safeRefusal = Number.isFinite(refusalRate) ? refusalRate : 0;
    const safeOngoing = ongoingAbsences || 0;

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                gap: 2,
                "& > *": { minWidth: 0 },
            }}
        >
            <Box sx={{ display: "flex" }}>
                <KpiCard
                    title="Demandes du mois"
                    value={safeMonthly}
                    progress={Math.min(100, safeMonthly)}
                />
            </Box>
            <Box sx={{ display: "flex" }}>
                <KpiCard
                    title="Delai moyen"
                    value={safeAvg.toFixed(1)}
                    suffix="j"
                    progress={Math.min(100, safeAvg * 10)}
                    color="info"
                />
            </Box>
            <Box sx={{ display: "flex" }}>
                <KpiCard
                    title="Taux de refus"
                    value={safeRefusal.toFixed(0)}
                    suffix="%"
                    progress={Math.min(100, safeRefusal)}
                    color="warning"
                />
            </Box>
            <Box sx={{ display: "flex" }}>
                <KpiCard
                    title="Absences en cours"
                    value={safeOngoing}
                    progress={Math.min(100, safeOngoing)}
                    color="success"
                />
            </Box>
        </Box>
    );
}
